import { useState } from 'react';
import dayjs from 'dayjs';

import { itemUnitsKey } from '@/api/masters';
import { useApiMutation } from '@/api/mutations';
import { purchaseOrdersKey } from '@/api/purchaseOrders';
import {
  createPurchaseBill,
  purchaseBillsKey,
  useBillable,
  usePurchaseBillPreview,
  type PurchaseBill,
} from '@/api/purchaseBills';
import { purchaseDeskKey, useSupplierDesk } from '@/api/purchaseDesk';
import { vendorPaymentsKey } from '@/api/vendorPayments';
import { ApiErrorAlert } from '@/components/ApiErrorAlert';
import { DatePicker } from '@/components/DatePicker';
import { DocumentHeading, DocumentLines, type DocumentColumn } from '@/components/DocumentSheet';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Field, controlClass } from '@/components/Field';
import { IconAction } from '@/components/IconAction';
import { Button, Card, Notice, SectionTitle } from '@/components/PageHeading';
import { toDayjs, toIsoDate } from '@core/format/dates';
import { formatAmount } from '@core/format/money';
import {
  addCharge,
  addReceiptLine,
  billChecks,
  billProblems,
  emptyBill,
  removeBillLine,
  toBillRequest,
  updateBillLine,
  type BillDraft,
  type BillLineDraft,
} from '@core/purchasing/bill';

const rupees = (value: number) => `₹${formatAmount(value)}`;

/** One row of the screen: a delivery line (ticked or not), or a charge. */
type Row = { kind: 'received'; receiptLineId: number } | { kind: 'charge'; key: string };

/**
 * Purchases › Their bill (canvas "Purchase order flow", 2026-10-25). Everything received and not yet
 * billed starts ticked; the clerk unticks what is not on the paper, types the number, the date and
 * the paper's total, and the server's preview says what it comes to, when it falls due, and whether
 * it agrees with the paper, the order's rate and what was received — three checks, each ticked or
 * naming what differs.
 */
export function BillScreen({
  vendorId,
  trail,
  onClose,
  onSaved,
}: {
  vendorId: number;
  /** Where Back goes, as the band's trail names it — the order it was opened from; else the supplier. */
  trail?: string | undefined;
  onClose: () => void;
  onSaved: (bill: PurchaseBill, payNow: boolean) => void;
}) {
  const today = dayjs().format('YYYY-MM-DD');
  const billable = useBillable(vendorId);
  const supplier = useSupplierDesk(vendorId).data?.supplier;
  const [draft, setDraft] = useState<BillDraft>(() => ({
    ...emptyBill(today),
    supplierVendorId: vendorId,
  }));
  const [seeded, setSeeded] = useState(false);
  const [payNow, setPayNow] = useState(false);
  const fields = useAutoFocusFirstField<HTMLDivElement>();

  // Everything delivered and not yet billed starts on the bill, once it has loaded.
  if (!seeded && billable.data) {
    setSeeded(true);
    setDraft((was) => billable.data.reduce(addReceiptLine, was));
  }

  const lineOf = (receiptLineId: number) =>
    draft.lines.find((line) => line.goodsReceiptLineId === receiptLineId);
  const problems = billProblems(draft);
  const preview = usePurchaseBillPreview(problems.length === 0 ? toBillRequest(draft) : null);
  const figures = problems.length === 0 && !preview.isError ? preview.data : undefined;

  const save = useApiMutation<PurchaseBill, boolean>({
    mutationFn: () => createPurchaseBill(toBillRequest(draft)),
    success: (saved) => `${saved.documentNumber} entered — ${rupees(saved.billTotal)}.`,
    invalidates: [
      purchaseBillsKey,
      purchaseOrdersKey,
      purchaseDeskKey,
      vendorPaymentsKey,
      itemUnitsKey,
    ],
    onSuccess: (saved, pay) => onSaved(saved, pay),
  });
  const busy = save.isPending;

  const rows: Row[] = [
    ...(billable.data ?? []).map((line) => ({
      kind: 'received' as const,
      receiptLineId: line.goodsReceiptLineId,
    })),
    ...draft.lines
      .filter((line) => line.goodsReceiptLineId === null)
      .map((line) => ({ kind: 'charge' as const, key: line.key })),
  ];

  const draftLine = (row: Row): BillLineDraft | undefined =>
    row.kind === 'received'
      ? lineOf(row.receiptLineId)
      : draft.lines.find((line) => line.key === row.key);
  const set = (line: BillLineDraft, change: Partial<Omit<BillLineDraft, 'key'>>) =>
    setDraft((was) => updateBillLine(was, line.key, change));
  const figure = (value: string) => (value === '' ? null : Number(value));
  const garments = draft.lines
    .filter((line) => line.goodsReceiptLineId !== null)
    .reduce((sum, line) => sum + line.quantity, 0);

  const columns: DocumentColumn<Row>[] = [
    {
      key: 'tick',
      title: '',
      render: (row) => {
        if (row.kind === 'charge') {
          return null;
        }

        const source = billable.data?.find((line) => line.goodsReceiptLineId === row.receiptLineId);
        const on = draftLine(row);

        return (
          <input
            type="checkbox"
            aria-label={`On this bill: ${source?.itemName ?? ''} ${source?.variantDisplayName ?? ''}`}
            checked={on !== undefined}
            disabled={busy}
            onChange={() =>
              setDraft((was) =>
                on ? removeBillLine(was, on.key) : source ? addReceiptLine(was, source) : was,
              )
            }
            className="size-4"
          />
        );
      },
    },
    {
      key: 'what',
      title: 'What',
      render: (row) => {
        const line = draftLine(row);

        if (row.kind === 'charge') {
          return line ? (
            <input
              aria-label="What the charge is for"
              value={line.description}
              disabled={busy}
              onChange={(event) => set(line, { description: event.target.value })}
              className={`${controlClass} min-w-40`}
            />
          ) : null;
        }

        const source = billable.data?.find((l) => l.goodsReceiptLineId === row.receiptLineId);

        return (
          <div className={line ? '' : 'opacity-60'}>
            <p className="mb-0 font-semibold text-gray-900">
              {source?.itemName} · {source?.variantDisplayName}
            </p>
            <p className="text-default mb-0 text-[length:var(--scale-list-meta)]">
              {source
                ? `Came ${dayjs(source.receivedOn).format('D MMM')} on ${source.goodsReceiptNumber}`
                : ''}
              {source && source.alreadyBilled > 0 ? ` · ${source.alreadyBilled} billed before` : ''}
            </p>
          </div>
        );
      },
    },
    {
      key: 'qty',
      title: 'Qty',
      numeric: true,
      render: (row) => {
        const line = draftLine(row);

        return line ? (
          <input
            type="number"
            aria-label="Quantity"
            onWheel={(event) => event.currentTarget.blur()}
            min={1}
            value={line.quantity}
            disabled={busy}
            onChange={(event) => set(line, { quantity: Number(event.target.value) })}
            className={`${controlClass} max-w-16 text-right`}
          />
        ) : (
          '—'
        );
      },
    },
    {
      key: 'rate',
      title: 'Rate (₹)',
      numeric: true,
      render: (row) => {
        const line = draftLine(row);

        return line ? (
          <input
            type="number"
            aria-label="Rate"
            onWheel={(event) => event.currentTarget.blur()}
            min={0}
            step="0.01"
            value={line.rate ?? ''}
            disabled={busy}
            onChange={(event) => set(line, { rate: figure(event.target.value) })}
            className={`${controlClass} max-w-28 text-right`}
          />
        ) : (
          '—'
        );
      },
    },
    {
      key: 'gst',
      title: 'GST %',
      numeric: true,
      render: (row) => {
        const line = draftLine(row);

        return line ? (
          <input
            type="number"
            aria-label="GST rate"
            onWheel={(event) => event.currentTarget.blur()}
            min={0}
            max={100}
            step="0.01"
            value={line.taxRatePct ?? ''}
            disabled={busy}
            onChange={(event) => set(line, { taxRatePct: figure(event.target.value) })}
            className={`${controlClass} max-w-20 text-right`}
          />
        ) : (
          '—'
        );
      },
    },
    {
      key: 'amount',
      title: 'Before tax',
      numeric: true,
      render: (row) => {
        const line = draftLine(row);

        return line && line.rate !== null ? (
          <span className="font-semibold">{rupees(line.quantity * line.rate)}</span>
        ) : (
          '—'
        );
      },
    },
    {
      key: 'remove',
      title: '',
      render: (row) => {
        const line = draftLine(row);

        return row.kind === 'charge' && line ? (
          <IconAction
            icon="icon icon-x"
            label="Remove the charge"
            onClick={() => setDraft((was) => removeBillLine(was, line.key))}
          />
        ) : null;
      },
    },
  ];

  const checks = figures ? billChecks(figures.differences, draft) : [];
  const charges = draft.lines.filter((line) => line.goodsReceiptLineId === null).length;
  const terms = figures
    ? `${figures.termDays} days${figures.supplierIsMsme ? ', inside the MSME 45-day limit' : ''}`
    : '';
  const saveWith = (pay: boolean) => {
    setPayNow(pay);
    void save.mutateAsync(pay).catch(() => undefined);
  };

  return (
    <>
      <DocumentHeading
        trail={trail ?? supplier?.name ?? 'Supplier'}
        current="Their bill"
        title="Their bill"
        subtitle="Everything that came and is not on a bill yet starts ticked. Untick what is not on their paper."
        onBack={onClose}
        actions={
          <Button icon="icon icon-arrow-left" shortcut="back" disabled={busy} onClick={onClose}>
            Back
          </Button>
        }
      />

      <div ref={fields} {...enterKeyScope} className="grid grid-cols-12 gap-4">
        <div className="col-span-12 xl:col-span-8">
          <Card>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Field label="Their bill number" required>
                <input
                  value={draft.supplierBillNumber}
                  disabled={busy}
                  onChange={(event) =>
                    setDraft((was) => ({ ...was, supplierBillNumber: event.target.value }))
                  }
                  className={controlClass}
                />
              </Field>
              <Field label="Bill date" required>
                <DatePicker
                  className="w-full"
                  value={toDayjs(draft.billDate) ?? null}
                  allowClear={false}
                  disabled={busy}
                  onChange={(value) =>
                    setDraft((was) => ({ ...was, billDate: toIsoDate(value) ?? was.billDate }))
                  }
                />
              </Field>
              <Field label="Total on their paper (₹)">
                <input
                  type="number"
                  onWheel={(event) => event.currentTarget.blur()}
                  min={0}
                  step="0.01"
                  value={draft.paperTotal ?? ''}
                  disabled={busy}
                  onChange={(event) =>
                    setDraft((was) => ({ ...was, paperTotal: figure(event.target.value) }))
                  }
                  className={controlClass}
                />
              </Field>
              <Field label="GST % on every line">
                <input
                  type="number"
                  onWheel={(event) => event.currentTarget.blur()}
                  min={0}
                  max={100}
                  step="0.01"
                  value={draft.defaultTaxRatePct ?? ''}
                  disabled={busy}
                  onChange={(event) => {
                    const pct = figure(event.target.value);

                    setDraft((was) => ({
                      ...was,
                      defaultTaxRatePct: pct,
                      lines: was.lines.map((line) => ({ ...line, taxRatePct: pct })),
                    }));
                  }}
                  className={controlClass}
                />
              </Field>
            </div>

            <div className="border-border-color mt-5 border-t pt-5">
              <SectionTitle icon="icon icon-file-text" hue="info">
                On this bill
              </SectionTitle>
              <DocumentLines
                columns={columns}
                rows={rows}
                rowKey={(row) => (row.kind === 'received' ? `r${row.receiptLineId}` : row.key)}
                empty={
                  billable.isLoading
                    ? 'Loading…'
                    : 'Nothing from this supplier is waiting to be billed.'
                }
              />
              <Button icon="icon icon-plus" disabled={busy} onClick={() => setDraft(addCharge)}>
                Add a charge — transport, packing
              </Button>
            </div>
          </Card>
        </div>

        <div className="col-span-12 flex flex-col gap-4 xl:col-span-4">
          <Card>
            <SectionTitle>What it comes to</SectionTitle>
            <Line
              label={`${garments} garment${garments === 1 ? '' : 's'}${
                charges > 0 ? ` and ${charges} charge${charges === 1 ? '' : 's'}` : ''
              }, before tax`}
              value={figures ? rupees(figures.taxableValue) : '—'}
            />
            {figures &&
              (figures.isInterState ? (
                <Line label="IGST" value={rupees(figures.igstAmount)} />
              ) : (
                <Line label="CGST + SGST" value={rupees(figures.cgstAmount + figures.sgstAmount)} />
              ))}
            <div className="border-border-color mt-2 flex items-baseline justify-between border-t pt-2">
              <span className="font-bold text-gray-900">Bill total</span>
              <span className="text-primary font-mono-nums text-xl font-bold">
                {figures ? rupees(figures.billTotal) : '—'}
              </span>
            </div>
            {figures && (
              <Line
                label="Falls due"
                value={`${dayjs(figures.dueDate).format('D MMM YYYY')} · ${terms}`}
              />
            )}

            {preview.isError && (
              <div className="mt-3">
                <ApiErrorAlert error={preview.error} />
              </div>
            )}
          </Card>

          <Card>
            <SectionTitle>Checked against</SectionTitle>
            {checks.length === 0 ? (
              <p className="text-default mb-0 text-[length:var(--scale-list-meta)]">
                Checked once the bill has its number, date and lines.
              </p>
            ) : (
              <ul className="m-0 flex list-none flex-col gap-2 ps-0">
                {checks.map((check) => (
                  <li key={check.kind} className="flex list-none items-start gap-2">
                    <span
                      className={`mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] ${
                        check.ok
                          ? 'bg-success-transparent text-[color:var(--color-success)]'
                          : 'bg-warning-transparent text-[color:var(--color-warning)]'
                      }`}
                      aria-hidden="true"
                    >
                      <i className={`icon ${check.ok ? 'icon-check' : 'icon-triangle-alert'}`} />
                    </span>
                    <div className="min-w-0 text-[length:var(--scale-list-meta)]">
                      <p className="mb-0 text-gray-900">
                        {check.label}
                        <span className="sr-only">{check.ok ? ' — yes' : ' — no'}</span>
                      </p>
                      {check.differences.map((difference) => (
                        <p key={difference} className="text-default mb-0">
                          {difference}
                        </p>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {checks.some((check) => !check.ok) && (
              <p className="text-default mt-2 mb-0 text-[length:var(--scale-list-meta)]">
                It does not agree, and can still be saved — the difference stays on the bill.
              </p>
            )}

            {problems.length > 0 && draft.lines.length > 0 && (
              <div className="mt-3">
                <Notice tone="warning" title="Not ready yet">
                  <ul className="mt-1 flex list-disc flex-col gap-0.5 ps-4">
                    {problems.slice(0, 6).map((problem) => (
                      <li key={problem}>{problem}</li>
                    ))}
                  </ul>
                </Notice>
              </div>
            )}

            <div className="mt-4 flex flex-col gap-2">
              <Button
                variant="primary"
                icon="icon icon-check"
                data-enter-key-commit
                shortcut="save"
                disabled={busy || !figures}
                onClick={() => saveWith(false)}
              >
                {busy && !payNow ? 'Saving…' : 'Save the bill'}
              </Button>
              <Button disabled={busy || !figures} onClick={() => saveWith(true)}>
                {busy && payNow ? 'Saving…' : 'Save and pay now'}
              </Button>
              <Button variant="secondary" disabled={busy} onClick={onClose}>
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}

function Line({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <p className="mb-1 flex justify-between text-[13px]">
      <span className="text-default">{label}</span>
      <span
        className={`font-mono-nums text-gray-900 ${strong ? 'text-[15px] font-bold' : 'font-semibold'}`}
      >
        {value}
      </span>
    </p>
  );
}
